import { useState, useEffect } from "react";
import axios from "axios";
import { Home, TreePine, Leaf, Minus, Plus, CheckCircle, Phone, User, PhoneCall } from "lucide-react";
import { API, PROPERTY_OPTIONS, SHRUB_OPTIONS } from "./constants";
import { formatUSD } from "../utils/currency";

export default function EstimateSection() {
  const [pricing, setPricing] = useState(null);
  const [property, setProperty] = useState("");
  const [shrub, setShrub] = useState("");
  const [extraShrubs, setExtraShrubs] = useState(0);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setPricing(r.data)).catch(() => {
      setPricing({ property_sizes: { up_to_1_acre: 60, "1_to_2_acres": 100, "2_to_3_acres": 140, "3_to_4_acres": 180, above_5_acres: 0 }, shrub_trimming: { none: 0, front_only: 40, front_1_side: 60, front_2_sides: 80, whole_house: 100 }, extra_shrub_price: 10 });
    });
  }, []);

  const isDirectContact = property === "above_5_acres";
  const total = pricing
    ? (pricing.property_sizes?.[property] || 0)
      + (pricing.shrub_trimming?.[shrub] || 0)
      + extraShrubs * (pricing.extra_shrub_price || 10)
    : 0;

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim() || !property) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates`, {
        name: name.trim(), phone: phone.trim(),
        property_size: property, shrub_trimming: shrub || "none",
        extra_shrubs: extraShrubs, estimated_total: total
      });
      setSubmitted(true);
    } catch (err) {
      console.warn("[estimate] lead submission failed (non-critical):", err?.message || err);
    }
    finally { setSubmitting(false); }
  };

  if (!pricing) return null;

  if (submitted) {
    return (
      <section className="py-20 md:py-28 bg-forest-700" data-testid="estimate-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-10 shadow-xl">
            <CheckCircle className="w-16 h-16 text-forest-500 mx-auto mb-4" />
            <h3 className="text-2xl font-serif font-bold text-forest-700 mb-2">Request Sent!</h3>
            <p className="text-stone-600">Thank you, {name}. A representative will contact you shortly to schedule an on-site visit.</p>
            <p className="text-2xl font-bold text-forest-600 mt-4">Estimated Value: {formatUSD(total)}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28 bg-forest-700" data-testid="estimate-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-forest-200 font-medium tracking-wider uppercase text-sm">Instant Quote</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white font-bold mt-3 mb-4">Get Your Free Estimate</h2>
          <p className="text-forest-200 max-w-2xl mx-auto">Select your property details below and get an instant price estimate in seconds.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Standard Lawn Maintenance */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-2 mb-4">
              <Leaf className="w-5 h-5 text-forest-200" />
              <h3 className="text-white font-semibold">Standard Lawn Maintenance</h3>
            </div>
            <div className="space-y-2">
              {PROPERTY_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  data-testid={`prop-${opt.key}`}
                  onClick={() => setProperty(opt.key)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    property === opt.key
                      ? "bg-forest-400 text-white shadow-lg scale-[1.02]"
                      : "bg-white/10 text-forest-100 hover:bg-white/20"
                  }`}
                >
                  {opt.label}
                  {opt.key === "above_5_acres" && <span className="float-right text-xs opacity-60">Contact us</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Shrub Trimming */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-2 mb-4">
              <TreePine className="w-5 h-5 text-forest-200" />
              <h3 className="text-white font-semibold">Shrub Trimming</h3>
            </div>
            <div className="space-y-2">
              {SHRUB_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  data-testid={`shrub-${opt.key}`}
                  onClick={() => setShrub(opt.key)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    shrub === opt.key
                      ? "bg-forest-400 text-white shadow-lg scale-[1.02]"
                      : "bg-white/10 text-forest-100 hover:bg-white/20"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Extra Shrubs + Contact Info */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-2 mb-4">
              <Leaf className="w-5 h-5 text-forest-200" />
              <h3 className="text-white font-semibold">Extra Shrubs & Info</h3>
            </div>
            <div className="mb-5">
              <label className="text-sm text-forest-200 mb-2 block">Isolated shrubs on property (${pricing.extra_shrub_price || 10} each)</label>
              <div className="flex items-center gap-3">
                <button data-testid="shrub-minus" onClick={() => setExtraShrubs(Math.max(0, extraShrubs - 1))} className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white transition-colors">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-2xl font-bold text-white w-12 text-center" data-testid="shrub-count">{extraShrubs}</span>
                <button data-testid="shrub-plus" onClick={() => setExtraShrubs(extraShrubs + 1)} className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-forest-200 mb-1 block">Your Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-300" />
                  <input data-testid="estimate-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-forest-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest-400" placeholder="John Doe" required />
                </div>
              </div>
              <div>
                <label className="text-sm text-forest-200 mb-1 block">Phone / WhatsApp *</label>
                <div className="relative">
                  <PhoneCall className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-300" />
                  <input data-testid="estimate-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-forest-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest-400" placeholder="(478) 555-1234" required />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Total & Submit */}
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl max-w-2xl mx-auto text-center">
          {isDirectContact ? (
            <div>
              <p className="text-lg text-stone-700 mb-3">Properties above 5 acres require a custom quote.</p>
              <a href="tel:+14783018896" className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors">
                <Phone className="w-5 h-5" /> Call Us: (478) 301-8896
              </a>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <span className="text-sm text-stone-500">Estimated Total</span>
                <p className="text-4xl font-bold text-forest-700 mt-1" data-testid="estimate-total">{formatUSD(total)}</p>
              </div>
              <button data-testid="estimate-submit" onClick={handleSubmit} disabled={submitting || !name.trim() || !phone.trim() || !property} className="bg-forest-600 hover:bg-forest-700 text-white px-8 py-3 rounded-xl font-semibold text-base transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? "Sending..." : "Send Request"}
              </button>
              <p className="text-xs text-stone-400 mt-4 max-w-md mx-auto">
                This is an estimate based on the information provided. An on-site visit by a representative is required to validate and confirm the final quote.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
